import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import {
  ArrowRight,
  BarChart3,
  Building2,
  Clock,
  Layers,
  Sparkles,
  Users,
} from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import { EinheitFreigabenCard } from "@/components/einheit-freigaben-card"
import { PageHeader } from "@/components/page-header"
import { Separator } from "@/components/ui/separator"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  useAnalyticsOverview,
  useDimensionAverages,
  useTeams,
  useTenants,
  useTrainingsPerDay,
} from "@/lib/queries"
import { useSessionContext } from "@/lib/session"

import { Link, createFileRoute } from "@tanstack/react-router";
// Recharts paints its tooltip white by default, which stays a white slab in
// the dark theme.
const TOOLTIP_STYLE = {
  contentStyle: {
    background: "var(--popover)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-md)",
    color: "var(--popover-foreground)",
    fontSize: 12,
  },
  labelStyle: { color: "var(--muted-foreground)" },
  itemStyle: { color: "var(--popover-foreground)" },
}

const PIE_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
]

function ManagementDashboard() {
  const { tenantId, role } = useSessionContext()
  const { data: tenants = [] } = useTenants()
  const { data: teams = [] } = useTeams()
  const { data: overview } = useAnalyticsOverview(30)
  const { data: perDay = [] } = useTrainingsPerDay(30)
  const { data: dimensions = [] } = useDimensionAverages(30)

  const sparkasse = tenants.find((tenant) => tenant.id === tenantId)

  // Figures for the last 30 days. Aggregates only by design: nobody in this
  // role sees individual conversations.
  const k = {
    trainingsThisMonth: overview?.sessions.total ?? 0,
    // Reported as plain training time, not as an IDD statement: how it counts
    // towards continuing education is still open.
    activeMinutes: overview?.idd.totalMinutes ?? 0,
    activeUsers: overview?.sessions.activeUsers ?? 0,
    seats: overview?.sessions.seats ?? 0,
    acceptance: overview?.acceptance.averageScore ?? 0,
  }

  // Weakest dimensions first: those are the learning areas.
  const topLearningFields = dimensions
    .slice()
    .sort((a, b) => a.average - b.average)
    .slice(0, 5)
    .map((dimension) => ({ label: dimension.label, count: dimension.average }))

  const trainingsOverTime = perDay.map((entry) => ({
    week: new Date(entry.day).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
    }),
    trainings: entry.sessions,
  }))

  const groupActivity = teams
    .filter((team) => team.users > 0)
    .map((team) => ({ label: team.name, value: team.users }))

  // Which phases get trained. Replaces the former product split: the phase is
  // the only training axis there is.
  const phaseSplit = (overview?.topPhases ?? []).map((phase) => ({
    label: phase.label,
    value: phase.sessions,
  }))

  const teamThemes = teams.map((team) => ({
    team: team.name,
    theme: team.focus,
    lead: team.lead,
  }))

  return (
    <div className="space-y-7">
      <PageHeader
        eyebrow={
          <>
            <Building2 className="h-3.5 w-3.5" />
            <span>{sparkasse?.name ?? "Sparkasse"}</span>
            <span className="h-1 w-1 rounded-full bg-primary" />
            <span className="text-foreground/60">Letzte 30 Tage</span>
          </>
        }
        title="Wirkung & Lernfelder"
        description="Aggregierte Sicht auf Trainingsaktivität, aktive Trainingszeit und Lernfelder dieser Sparkasse. Keine Einzelfall-Einsicht."
      />

      <section className="grid gap-3 md:grid-cols-4">
        <Kpi
          icon={<Sparkles className="h-3.5 w-3.5" />}
          label="Trainings · 30 Tage"
          value={k.trainingsThisMonth.toLocaleString("de-DE")}
          rev="reveal-1"
        />
        <Kpi
          icon={<Clock className="h-3.5 w-3.5" />}
          label="Aktive Trainingszeit"
          value={`${k.activeMinutes.toLocaleString("de-DE")} Min.`}
          rev="reveal-2"
        />
        <Kpi
          icon={<Users className="h-3.5 w-3.5" />}
          label="Aktive Berater:innen"
          value={
            k.seats
              ? `${k.activeUsers.toLocaleString("de-DE")} / ${k.seats.toLocaleString("de-DE")}`
              : k.activeUsers.toLocaleString("de-DE")
          }
          rev="reveal-3"
        />
        <Kpi
          icon={<BarChart3 className="h-3.5 w-3.5" />}
          label="Feedback-Bewertung"
          value={k.acceptance ? `${k.acceptance} / 5` : "–"}
          rev="reveal-4"
        />
      </section>

      <Link to="/management/teams"
        className="card-lift reveal flex items-center justify-between rounded-md border border-[var(--rule)] bg-card p-5"
      >
        <div className="flex items-center gap-4">
          <div className="flex h-11 w-11 items-center justify-center rounded-md bg-accent text-primary">
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-[17px] leading-tight font-medium">
              Administration von Sparkassen &amp; Gruppen
            </div>
            <p className="mt-1 text-[13.5px] leading-relaxed text-muted-foreground">
              Mandanten, Gruppen, Trainingsfreigaben und aggregierte
              Verantwortlichkeiten verwalten.
            </p>
          </div>
        </div>
        <span className="flex items-center gap-1 font-bold text-[11.5px] tracking-[0.06em] text-muted-foreground uppercase">
          Öffnen <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </Link>

      <section className="grid gap-4 lg:grid-cols-3">
        <Card className="gap-2 p-5 lg:col-span-2">
          <Heading
            icon={<BarChart3 className="h-3.5 w-3.5" />}
            title="Top-Lernfelder"
          />
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topLearningFields}
                layout="vertical"
                margin={{ left: 24, right: 16 }}
              >
                <CartesianGrid horizontal={false} stroke="var(--rule)" />
                <XAxis type="number" stroke="var(--muted-foreground)" fontSize={11} />
                <YAxis
                  type="category"
                  dataKey="label"
                  stroke="var(--muted-foreground)"
                  fontSize={11}
                  width={210}
                />
                <Tooltip cursor={{ fill: "var(--accent)" }} {...TOOLTIP_STYLE} />
                <Bar dataKey="count" fill="var(--chart-1)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="gap-2 p-5">
          <Heading
            icon={<Users className="h-3.5 w-3.5" />}
            title="Zielgruppenverteilung"
          />
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={groupActivity}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                >
                  {groupActivity.map((_entry, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip {...TOOLTIP_STYLE} />
                <Legend
                  iconType="circle"
                  wrapperStyle={{ fontSize: 11 }}
                  align="left"
                  layout="vertical"
                  verticalAlign="middle"
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <Card className="gap-2 p-5 lg:col-span-2">
          <Heading
            icon={<Clock className="h-3.5 w-3.5" />}
            title="Trainingsaktivität pro Tag"
          />
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trainingsOverTime}>
                <CartesianGrid stroke="var(--rule)" vertical={false} />
                <XAxis dataKey="week" stroke="var(--muted-foreground)" fontSize={11} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} />
                <Tooltip {...TOOLTIP_STYLE} />
                <Line
                  type="monotone"
                  dataKey="trainings"
                  stroke="var(--chart-1)"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="gap-2 p-5">
          <Heading
            icon={<Layers className="h-3.5 w-3.5" />}
            title="Trainierte Phasen"
          />
          <div className="space-y-2.5 pt-1">
            {phaseSplit.map((row) => {
              const max = Math.max(
                ...phaseSplit.map((r) => r.value)
              )
              return (
                <div key={row.label}>
                  <div className="flex justify-between text-[13.5px]">
                    <span>{row.label}</span>
                    <span className="font-medium tabular-nums">
                      {row.value}
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 rounded bg-muted">
                    <div
                      className="h-1.5 rounded bg-primary"
                      style={{ width: `${(row.value / max) * 100}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </Card>
      </section>

      <Card className="p-0">
        <div className="flex items-center justify-between p-5 pb-3">
          <h3 className="text-[15px] font-semibold">Themen pro Gruppe</h3>
          <Badge variant="secondary">aggregiert</Badge>
        </div>
        <Separator />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Gruppe</TableHead>
              <TableHead>Lernfokus</TableHead>
              <TableHead className="text-right">Leitung</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {teamThemes.map((row) => (
              <TableRow key={row.team}>
                <TableCell className="font-medium">{row.team}</TableCell>
                <TableCell className="text-[13.5px] text-muted-foreground">
                  {row.theme}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {row.lead}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {tenantId && (role === "manager" || role === "platform") && (
        <EinheitFreigabenCard tenantId={tenantId} readOnly />
      )}
    </div>
  )
}

function Kpi({
  icon,
  label,
  value,
  rev,
}: {
  icon: React.ReactNode
  label: string
  value: string
  rev?: string
}) {
  return (
    <Card className={"reveal " + (rev ?? "") + " gap-0 border-[var(--rule)] p-5"}>
      <div className="flex items-center gap-1.5 font-bold text-[11px] tracking-[0.06em] text-muted-foreground uppercase">
        {icon}
        {label}
      </div>
      <div className="tnum mt-3 font-display text-[36px] leading-none font-medium tracking-tight">
        {value}
      </div>
    </Card>
  )
}

function Heading({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-1.5 font-bold text-[11.5px] tracking-[0.06em] text-muted-foreground uppercase">
      {icon}
      {title}
    </div>
  )
}

export const Route = createFileRoute("/_app/management/")({
  component: ManagementDashboard,
});
